import alert_mixin from '@/utils/mixins/alert_mixin';
import {
  assign_dependent_question,
  create_question,
  fetch_question_for_edit,
  fetch_questions_in_project,
  update_question,
} from '@/services/question_service';
import DependentQuestionsPanel from  "@/components/config_components/DependentQuestions/DependentQuestionsPanel.vue"

export default {
  name: 'ManageQuestions',
  mixins: [alert_mixin],
  components: {
    DependentQuestionsPanel,
  },
  data: () => ({
    pageTitle: 'Question Management Hub',
    pageSubtitle: 'Create Questions',
    pageDescription: 'Define new logic points for questions in your form.',
    searchQuery: '',
    selectedType: 'all',
    create_question_running: false,
    edit_question_running: false,
    questions_loading: false,
    edit_question_modal_open: false,
    questionForm: {
      question_text: '',
      question_description: '',
      question_number: '',
      question_type_id: '',
      is_mandatory: '',
      show_option_inputs: false,
      option_inputs: ['', ''],
      dependant_questions: [],
    },
    current_edit_question: {
      question_id: null,
      question_text: '',
      question_description: '',
      question_number: '',
      question_type_id: '',
      is_mandatory: '',
      show_option_inputs: false,
      option_inputs: ['', ''],
      dependant_questions: [],
    },
    questions: [],
    pagination: {
      current: 1,
      perPage: 10,
    },
  }),
  computed: {
    questionTypes() {
      return (this.$store.getters.questionTypes || []).map((type) => ({
        ...type,
        raw_type: type.question_type || type.formatted_type || '',
        label: this.format_question_type_label(type.question_type || type.formatted_type || ''),
      }));
    },
    questionTypesLoading() {
      return this.$store.getters.formMetadataLoading;
    },
    filteredQuestions() {
      const query = String(this.searchQuery || '').trim().toLowerCase();
      const type = String(this.selectedType || '').trim().toLowerCase();

      return this.questions.filter((question) => {
        const matchesQuery =
          !query ||
          String(question.question || '').toLowerCase().includes(query) ||
          String(question.options || '').toLowerCase().includes(query) ||
          String(question.subtitle || '').toLowerCase().includes(query);
        const matchesType = type === 'all' || String(question.type || '').toLowerCase() === type;

        return matchesQuery && matchesType;
      });
    },
    paginationPages() {
      const total_pages = Math.max(
        1,
        Math.ceil(this.filteredQuestions.length / this.pagination.perPage)
      );

      return Array.from({ length: total_pages }, (_, index) => index + 1);
    },
    paginatedQuestions() {
      const start_index = (this.pagination.current - 1) * this.pagination.perPage;
      return this.filteredQuestions.slice(start_index, start_index + this.pagination.perPage);
    },
    paginationSummary() {
      const total = this.filteredQuestions.length;

      if (!total) {
        return 'Showing 0 of 0 questions';
      }

      const start = (this.pagination.current - 1) * this.pagination.perPage + 1;
      const end = Math.min(start + this.pagination.perPage - 1, total);

      return `Showing ${start} to ${end} of ${total} questions`;
    },

    question_form_trigger_conditions() {
      if (!this.questionForm?.show_option_inputs) {
        return [];
      }

      return (Array.isArray(this.questionForm.option_inputs) ? this.questionForm.option_inputs : [])
        .map((option) => String(option || '').trim())
        .filter(Boolean);
    },
  },
  async created() {
    await Promise.all([
      this.$store.dispatch('ensureFormMetadataLoaded'),
      this.load_questions(),
    ]);
  },
  methods: {
    format_question_type_label(value) {
      const normalized_value =
        value && typeof value === 'object'
          ? value.type || value.question_type || value.formatted_type || ''
          : value;

      return String(normalized_value || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (letter) => letter.toUpperCase())
        .trim();
    },

    get_selected_question_type(question_type_id) {
      return this.questionTypes.find(
        (type) => String(type.id) === String(question_type_id)
      );
    },

    get_selected_question_raw_type(question_type_id) {
      return String(this.get_selected_question_type(question_type_id)?.raw_type || '').toLowerCase();
    },

    build_question_request_payload() {
      const route_query = this.$route?.query || {};
      const payload = {};

      if (route_query.project_id) {
        payload.project_id = route_query.project_id;
      }

      if (route_query.form_id) {
        payload.form_id = route_query.form_id;
      }

      return payload;
    },

    handle_question_type_change() {
      const normalized_type = this.get_selected_question_raw_type(this.questionForm.question_type_id);
      const show_option_inputs =
        normalized_type.includes('single_select') || normalized_type.includes('multi_select');

      this.questionForm.show_option_inputs = show_option_inputs;

      if (show_option_inputs && (!Array.isArray(this.questionForm.option_inputs) || !this.questionForm.option_inputs.length)) {
        this.questionForm.option_inputs = ['', ''];
      }

      if (!show_option_inputs) {
        this.questionForm.option_inputs = ['', ''];
        this.questionForm.dependant_questions = [];
      }

      if (show_option_inputs && !Array.isArray(this.questionForm.dependant_questions)) {
        this.questionForm.dependant_questions = [];
      }
    },

    handle_question_type_change_for_edit(form) {
      const normalized_type = this.get_selected_question_raw_type(form.question_type_id);
      const show_option_inputs =
        normalized_type.includes('single_select') || normalized_type.includes('multi_select');

      form.show_option_inputs = show_option_inputs;

      if (show_option_inputs && (!Array.isArray(form.option_inputs) || !form.option_inputs.length)) {
        form.option_inputs = ['', ''];
      }

      if (!show_option_inputs) {
        form.option_inputs = ['', ''];
      }
    },

    build_question_row(question, index = 0) {
      const normalized_number = Number(question?.question_number);
      const option_sources =
        question?.multi_select_choices ??
        question?.multi_select_options ??
        question?.options ??
        question?.choices ??
        [];
      const option_values = Array.isArray(option_sources)
        ? option_sources
            .map((choice) => {
              if (choice && typeof choice === 'object') {
                return String(
                  choice.choice ??
                    choice.value ??
                    choice.option ??
                    choice.name ??
                    ''
                ).trim();
              }

              return String(choice || '').trim();
            })
            .filter(Boolean)
        : [];
      const assigned_as_dependent = Boolean(question?.is_assigned_as_dependent);
      const raw_status = String(question?.tag || '').trim();
      const question_type_value =
        question?.question_type?.type ||
        question?.question_type?.question_type ||
        question?.question_type ||
        question?.type ||
        '';
      const question_number =
        Number.isFinite(normalized_number) && String(question?.question_number || '').trim() !== ''
          ? String(normalized_number).padStart(2, '0')
          : String(index + 1).padStart(2, '0');
      const status_tone =
        raw_status.toLowerCase().includes('draft') || assigned_as_dependent ? 'draft' : 'active';

      return {
        id: question?.id ?? index + 1,
        number: question_number,
        question: String(question?.question_text || 'Untitled Question'),
        subtitle: assigned_as_dependent ? 'Dependent question' : 'Standalone question',
        type: this.format_question_type_label(question_type_value),
        options: option_values.length ? option_values.join(', ') : 'No options',
        status: raw_status || 'Active',
        statusTone: status_tone,
      };
    },

    async load_questions() {
      this.questions_loading = true;

      try {
        const rows = await fetch_questions_in_project(this.build_question_request_payload());
        this.questions = rows.map((question, index) => this.build_question_row(question, index));
        this.pagination.current = 1;
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          'Failed to load questions.';
        this.questions = [];
        this.show_error(message);
      } finally {
        this.questions_loading = false;
      }
    },

    build_option_inputs_from_values(values = []) {
      const options = Array.isArray(values)
        ? values
            .map((option) => {
              if (option && typeof option === 'object') {
                return String(
                  option.choice ??
                  option.value ??
                  option.option ??
                  option.name ??
                  option.label ??
                  option.text ??
                  ''
                ).trim();
              }

              return String(option || '').trim();
            })
            .filter(Boolean)
        : [];

      return options.length >= 2 ? options : ['', ''];
    },

    extract_question_option_values(question = {}) {
      return question?.options ||
        question?.multi_select_options ||
        question?.multi_select_choices ||
        question?.choices ||
        question?.question_options ||
        question?.option_values ||
        question?.question_choices ||
        [];
    },

    build_dependent_question_form(question = {}, index = 0) {
      const question_type_id = question?.question_type_id ?? question?.question_type?.id ?? '';
      const option_values = this.extract_question_option_values(question);
      const option_inputs = this.build_option_inputs_from_values(option_values);
      const normalized_type = this.get_selected_question_raw_type(question_type_id);
      const show_option_inputs =
        normalized_type.includes('single_select') || normalized_type.includes('multi_select');

      return {
        id: question?.id ?? `${Date.now()}-${index}`,
        question_text: String(question?.question_text || ''),
        question_description: String(question?.question_description || ''),
        question_type_id: question_type_id,
        is_mandatory: question?.is_mandatory ?? true,
        question_number: question?.question_number ?? '',
        multi_select_options: Array.isArray(question?.multi_select_options)
          ? question.multi_select_options
          : option_inputs.filter(Boolean),
        trigger_condition: question?.trigger_condition ?? null,
        dependant_questions: Array.isArray(question?.dependant_questions)
          ? question.dependant_questions.map((dep, dep_index) => this.build_dependent_question_form(dep, dep_index))
          : [],
        show_option_inputs,
        option_inputs,
        options: Array.isArray(option_values)
          ? option_values.map((option) =>
              option && typeof option === 'object'
                ? {
                    choice: String(
                      option.choice ??
                      option.value ??
                      option.option ??
                      option.name ??
                      option.label ??
                      option.text ??
                      ''
                    ),
                  }
                : {
                    choice: String(option || ''),
                  }
            )
          : [],
      };
    },

    async open_edit_question_modal(question) {
      const question_id = Number(question?.id);

      if (!question_id) {
        this.show_error('Please select a question to edit.');
        return;
      }

      this.edit_question_running = true;
      const loader = await this.show_loader('Loading question...');

      try {
        const parsed_response = await fetch_question_for_edit(question_id);
        const question_data = parsed_response?.data || {};
        const normalized_question = this.build_dependent_question_form(question_data);

        this.current_edit_question = {
          ...normalized_question,
          question_id,
          question_type_id:
            question_data?.question_type?.id ?? question_data?.question_type_id ?? normalized_question.question_type_id,
          is_mandatory:
            typeof question_data?.is_mandatory === 'boolean'
              ? question_data.is_mandatory
              : normalized_question.is_mandatory,
          question_number:
            question_data?.question_number ?? normalized_question.question_number ?? '',
          question_description:
            question_data?.question_description ?? normalized_question.question_description ?? '',
          question_text:
            question_data?.question_text ?? normalized_question.question_text ?? '',
          dependant_questions: Array.isArray(question_data?.dependant_questions)
            ? question_data.dependant_questions.map((dep, dep_index) =>
                this.build_dependent_question_form(dep, dep_index)
              )
            : normalized_question.dependant_questions,
        };

        this.handle_question_type_change_for_edit(this.current_edit_question);
        this.edit_question_modal_open = true;
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          'Failed to load question details.';
        this.show_error(message);
      } finally {
        await this.close_loader(loader);
        this.edit_question_running = false;
      }
    },

    close_edit_question_modal() {
      this.edit_question_modal_open = false;
      this.current_edit_question = {
        question_id: null,
        question_text: '',
        question_description: '',
        question_number: '',
        question_type_id: '',
        is_mandatory: '',
        show_option_inputs: false,
        option_inputs: ['', ''],
        dependant_questions: [],
      };
    },

    add_option_input(form) {
      if (!Array.isArray(form.option_inputs)) {
        form.option_inputs = [];
      }

      form.option_inputs.push('');
    },

    remove_option_input(form, option_index) {
      if (!Array.isArray(form.option_inputs) || form.option_inputs.length <= 2) {
        return;
      }

      form.option_inputs.splice(option_index, 1);
    },

    add_dependent_question(form) {
      if (!Array.isArray(form.dependant_questions)) {
        form.dependant_questions = [];
      }

      form.dependant_questions.push({
        id: `${Date.now()}-${form.dependant_questions.length}`,
        question_text: '',
        question_description: '',
        question_type_id: '',
        is_mandatory: true,
        question_number: '',
        trigger_condition: '',
        show_option_inputs: false,
        option_inputs: ['', ''],
        options: [],
        dependant_questions: [],
      });
    },

    remove_dependent_question(form, dep_index) {
      if (!Array.isArray(form.dependant_questions) || form.dependant_questions.length < 1) {
        return;
      }

      form.dependant_questions.splice(dep_index, 1);
    },

    serialize_dependent_questions(dependant_questions) {
      if (!Array.isArray(dependant_questions)) {
        return [];
      }

      return dependant_questions
        .map((dep_question) => {
          const normalized_type = this.get_selected_question_raw_type(dep_question.question_type_id);
          const requires_options =
            normalized_type.includes('single_select') || normalized_type.includes('multi_select');

          return {
            question_text: String(dep_question.question_text || '').trim(),
            question_description: String(dep_question.question_description || '').trim() || null,
            question_type_id: Number(dep_question.question_type_id),
            is_mandatory: Boolean(dep_question.is_mandatory),
            question_number: Number.parseInt(dep_question.question_number, 10) || null,
            options: requires_options && Array.isArray(dep_question.options)
              ? dep_question.options
                  .map((option) => String(option?.choice || option || '').trim())
                  .filter(Boolean)
              : [],
            trigger_condition: String(dep_question.trigger_condition || '').trim() || null,
            dependant_questions: [],
          };
        })
        .filter((dep) => dep.question_text && dep.question_type_id && dep.trigger_condition);
    },

    handle_dependent_question_type_change(dep_question) {
      const normalized_type = this.get_selected_question_raw_type(dep_question.question_type_id);
      const show_option_inputs =
        normalized_type.includes('single_select') || normalized_type.includes('multi_select');

      dep_question.show_option_inputs = show_option_inputs;

      if (show_option_inputs && (!Array.isArray(dep_question.options) || !dep_question.options.length)) {
        dep_question.options = [{ choice: '' }, { choice: '' }];
      }

      if (!show_option_inputs) {
        dep_question.options = [];
      }
    },

    add_dependent_option(dep_question) {
      if (!Array.isArray(dep_question.options)) {
        dep_question.options = [];
      }

      dep_question.options.push({ choice: '' });
    },

    remove_dependent_option(dep_question, option_index) {
      if (!Array.isArray(dep_question.options) || dep_question.options.length <= 2) {
        return;
      }

      dep_question.options.splice(option_index, 1);
    },

    build_question_payload(form) {
      const normalized_type = this.get_selected_question_raw_type(form.question_type_id);
      const requires_options =
        normalized_type.includes('single_select') || normalized_type.includes('multi_select');
      const option_values = requires_options
        ? (Array.isArray(form.option_inputs) ? form.option_inputs : [])
            .map((option) => String(option || '').trim())
            .filter(Boolean)
        : [];

      return {
        question_text: String(form.question_text || '').trim(),
        question_description: String(form.question_description || '').trim() || null,
        question_type_id: Number(form.question_type_id),
        is_mandatory: Boolean(form.is_mandatory),
        question_number: Number.isInteger(form.question_number) ? form.question_number : null,
        multi_select_options: option_values,
        section_id: null,
        dependant_questions: [],
        trigger_condition: null,
      };
    },

    build_edit_question_payload() {
      const normalized_type = this.get_selected_question_raw_type(this.current_edit_question.question_type_id);
      const requires_options =
        normalized_type.includes('single_select') || normalized_type.includes('multi_select');
      const option_values = requires_options
        ? (Array.isArray(this.current_edit_question.option_inputs)
            ? this.current_edit_question.option_inputs
            : [])
            .map((option) => String(option || '').trim())
            .filter(Boolean)
        : [];

      return {
        question_id: this.current_edit_question.question_id,
        question_text: String(this.current_edit_question.question_text || '').trim(),
        question_description: String(this.current_edit_question.question_description || '').trim() || null,
        question_number: Number.isInteger(this.current_edit_question.question_number)
          ? this.current_edit_question.question_number
          : Number.parseInt(this.current_edit_question.question_number, 10) || null,
        question_type_id: Number(this.current_edit_question.question_type_id),
        is_mandatory: Boolean(this.current_edit_question.is_mandatory),
        multi_select_options: option_values,
        dependant_questions: Array.isArray(this.current_edit_question.dependant_questions)
          ? this.current_edit_question.dependant_questions.map((dep_question) => ({
              question_text: String(dep_question.question_text || '').trim(),
              question_description: String(dep_question.question_description || '').trim() || null,
              question_type_id: Number(dep_question.question_type_id),
              is_mandatory: Boolean(dep_question.is_mandatory),
              question_number: Number.parseInt(dep_question.question_number, 10) || null,
              options: Array.isArray(dep_question.options)
                ? dep_question.options
                    .map((option) => String(option?.choice || option || '').trim())
                    .filter(Boolean)
                : [],
              trigger_condition: dep_question.trigger_condition || null,
              dependant_questions: [],
            }))
          : [],
        trigger_condition: null,
      };
    },

    async create_question() {
      const question_text = String(this.questionForm.question_text || '').trim();
      const question_type_id = String(this.questionForm.question_type_id || '').trim();
      const is_mandatory = this.questionForm.is_mandatory;
      const normalized_type = this.get_selected_question_raw_type(question_type_id);
      const supports_dependants =
        normalized_type.includes('single_select') || normalized_type.includes('multi_select');

      if (!question_text) {
        this.show_error('Please enter a question text.');
        return;
      }

      if (!question_type_id) {
        this.show_error('Please select a question type.');
        return;
      }

      if (is_mandatory === '') {
        this.show_error('Please select whether the question is mandatory.');
        return;
      }

      if (supports_dependants && Array.isArray(this.questionForm.dependant_questions) && this.questionForm.dependant_questions.length) {
        const invalid_dep = this.questionForm.dependant_questions.find((dep) => {
          const dep_text = String(dep?.question_text || '').trim();
          const dep_type_id = String(dep?.question_type_id || '').trim();
          const trigger = String(dep?.trigger_condition || '').trim();
          return !dep_text || !dep_type_id || !trigger;
        });

        if (invalid_dep) {
          this.show_error('Please complete all required fields for dependent questions (text, type, and trigger condition).');
          return;
        }
      }

      this.create_question_running = true;

      const loader = await this.show_loader('Saving question...');

      try {
        const dependent_questions = supports_dependants
          ? this.serialize_dependent_questions(this.questionForm.dependant_questions)
          : [];

        const response = await create_question(this.build_question_payload(this.questionForm));

        if (response?.status !== 'success') {
          this.show_error(response?.message || 'Failed to create question.');
          return;
        }

        const created_question_id =
          response?.data?.question_id ??
          response?.data?.id ??
          response?.data?.question?.id ??
          response?.question_id ??
          response?.id ??
          null;

        if (created_question_id && dependent_questions.length) {
          for (const dep of dependent_questions) {
            const dep_normalized_type = this.get_selected_question_raw_type(dep.question_type_id);
            const dep_requires_options =
              dep_normalized_type.includes('single_select') || dep_normalized_type.includes('multi_select');

            const assign_payload = {
              main_question_id: Number(created_question_id),
              dependent_data: {
                question_text: dep.question_text,
                question_type_id: dep.question_type_id,
                is_mandatory: dep.is_mandatory,
                trigger_condition: dep.trigger_condition,
                question_number: dep.question_number,
                multi_select_options: dep_requires_options ? dep.options : [],
              },
            };

            const assign_response = await assign_dependent_question(assign_payload);

            if (assign_response?.status !== 'success') {
              this.show_error(assign_response?.message || 'Failed to assign a dependent question.');
              return;
            }
          }
        }

        if (!created_question_id && dependent_questions.length) {
          this.show_error('Question created, but dependent questions could not be assigned (missing main_question_id in API response).');
          return;
        }

        this.questionForm.question_text = '';
        this.questionForm.question_description = '';
        this.questionForm.question_number = '';
        this.questionForm.question_type_id = '';
        this.questionForm.is_mandatory = '';
        this.questionForm.show_option_inputs = false;
        this.questionForm.option_inputs = ['', ''];
        this.questionForm.dependant_questions = [];
        this.selectedType = 'all';

        await this.load_questions();

        this.show_success(response.message || 'Question created successfully.');
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          'Failed to create question.';
        this.show_error(message);
      } finally {
        await this.close_loader(loader);
        this.create_question_running = false;
      }
    },

    async save_edited_question() {
      const question_text = String(this.current_edit_question.question_text || '').trim();
      const question_type_id = String(this.current_edit_question.question_type_id || '').trim();

      if (!this.current_edit_question.question_id) {
        this.show_error('Please select a question to edit.');
        return;
      }

      if (!question_text) {
        this.show_error('Please enter a question text.');
        return;
      }

      if (!question_type_id) {
        this.show_error('Please select a question type.');
        return;
      }

      if (this.current_edit_question.is_mandatory === '') {
        this.show_error('Please select whether the question is mandatory.');
        return;
      }

      this.edit_question_running = true;
      const loader = await this.show_loader('Saving changes...');

      try {
        const response = await update_question(this.build_edit_question_payload());

        if (response?.status !== 'success') {
          this.show_error(response?.message || 'Failed to update question.');
          return;
        }

        await this.load_questions();
        this.close_modal('edit_question_modal');
        this.show_success(response.message || 'Question updated successfully.');
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          'Failed to update question.';
        this.show_error(message);
      } finally {
        await this.close_loader(loader);
        this.edit_question_running = false;
      }
    },

    set_type_filter(type) {
      this.selectedType = type === 'All Types' ? 'all' : String(type || '').trim();
      this.pagination.current = 1;
    },

    set_page(page) {
      this.pagination.current = Math.max(1, Math.min(page, this.paginationPages.length));
    },
  },
  watch: {
    searchQuery() {
      this.pagination.current = 1;
    },
  },
};
